import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  Job,
  JobCompleteRequest,
  JobCreateRequest,
  JobListQuery,
  JobListResult,
  JobStartRequest,
  JobStatus,
  JobUpdateRequest
} from '../models/job.model';

@Injectable({ providedIn: 'root' })
export class JobService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/jobs`;

  private static readonly TIMEOUT_MS = 45000;

  getJobs(query: JobListQuery = {}): Observable<JobListResult> {
    let params = new HttpParams();
    if (query.status) params = params.set('status', query.status);
    if (query.dealerId) params = params.set('dealerId', query.dealerId);
    if (query.technicianId) params = params.set('technicianId', query.technicianId);
    if (query.scheduledFrom) params = params.set('scheduledFrom', query.scheduledFrom);
    if (query.scheduledTo) params = params.set('scheduledTo', query.scheduledTo);
    if (query.page) params = params.set('page', query.page);
    if (query.pageSize) params = params.set('pageSize', query.pageSize);

    return this.http.get<JobListResult>(this.baseUrl, { params });
  }

  getJobById(jobId: number): Observable<Job> {
    return this.http.get<Job>(`${this.baseUrl}/${jobId}`);
  }

  createJob(request: JobCreateRequest): Observable<Job> {
    return this.http.post<Job>(this.baseUrl, request);
  }

  updateJob(jobId: number, request: JobUpdateRequest): Observable<Job> {
    return this.http.put<Job>(`${this.baseUrl}/${jobId}`, request);
  }

  updateJobStatus(jobId: number, status: JobStatus): Observable<Job> {
    return this.http.patch<Job>(`${this.baseUrl}/${jobId}/status`, { status });
  }

  deactivateJob(jobId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${jobId}`);
  }

  startJob(jobId: number, request: JobStartRequest): Observable<Job> {
    return this.http.post<Job>(`${this.baseUrl}/${jobId}/start`, request).pipe(timeout(JobService.TIMEOUT_MS));
  }

  pauseJob(jobId: number): Observable<Job> {
    return this.http.post<Job>(`${this.baseUrl}/${jobId}/pause`, {});
  }

  resumeJob(jobId: number): Observable<Job> {
    return this.http.post<Job>(`${this.baseUrl}/${jobId}/resume`, {});
  }

  completeJob(jobId: number, request: JobCompleteRequest): Observable<Job> {
    return this.http.post<Job>(`${this.baseUrl}/${jobId}/complete`, request).pipe(timeout(JobService.TIMEOUT_MS));
  }
}
